import {
  DataTableRowActionsContainer,
  DataTableRowActionsProps,
} from '../../../components/Common/DataTable/DataTableRowActions';
import {
  DatabaseOfferWithApprovalRequest,
  OfferState,
  OfferStep,
} from '../../../../../shared/types/offers';
import { useHistory } from 'react-router-dom';
import useUserContext from '../../../providers/User/UserProvider';
import { CUSTOMER_RESPONDED_OFFER_STATES } from '../../../../../shared/constants';
import React, { useCallback, useState } from 'react';
import {
  ActionMenu,
  ActionMenuItem,
} from '../../../components/Common/ActionMenu';
import { IconButton } from '@mui/material';
import {
  Edit,
  MoreVert,
  PictureAsPdf,
  RemoveRedEye,
  PersonAddAlt1,
} from '@mui/icons-material';
import { RenderComponentPropsParent } from './OfferSheetsTable';
import DefaultTooltip from '../../../components/Tooltips/DefaultTooltip';
import {
  useOfferItemsPdf,
  useOfferPdf,
} from '../../../hooks/useOfferPdf';
import ResponsiblePersonDialog from './OfferResponsiblePersonDialog';

type OffersTableRowActionsProps = RenderComponentPropsParent<
  DataTableRowActionsProps<DatabaseOfferWithApprovalRequest>['renderRowActions']
>;

export const OffersTableRowActions: OffersTableRowActionsProps = (
  { item, infoToggle },
  { loading, refetch },
) => {
  const history = useHistory();
  const { userEmail: currentUserEmail } = useUserContext();
  const [
    responsiblePersonDialogOpen,
    setResponsiblePersonDialogOpen,
  ] = useState(false);
  const handleResponsiblePersonDialogClose = () => {
    setResponsiblePersonDialogOpen(false);
  };
  const {
    id,
    name,
    state,
    userEmail: offerUserEmail,
    archivedAt,
  } = item;
  // determines the label of the download pdf button and whether the pdf will include a draft watermark
  const isDraft = ![
    ...CUSTOMER_RESPONDED_OFFER_STATES,
    OfferState.Sent,
    OfferState.ReadyForSending,
  ].includes(state);

  // download offer pdf query
  const { refetch: downloadPdf, isFetching: isFetchingPdf } =
    useOfferPdf({
      id: String(id),
      draft: isDraft,
      pdfName: name,
    });
  // download offer items pdf query
  const {
    refetch: downloadItemsPdf,
    isFetching: isFetchingItemsPdf,
  } = useOfferItemsPdf({
    id: String(id),
    draft: isDraft,
    pdfName: name,
  });

  const editDisabled =
    offerUserEmail !== currentUserEmail ||
    state === OfferState.ReadyForSending ||
    state === OfferState.Sent ||
    state === OfferState.Expired ||
    state === OfferState.Approved ||
    state === OfferState.Rejected ||
    archivedAt;
  const handleEdit = useCallback(() => {
    const editPath =
      state === OfferState.InternalApproval
        ? `/tarjous/${id}?t=${OfferStep.Summary}`
        : `/tarjous/${id}?t=${OfferStep.Pricing}`;
    history.push(editPath);
  }, [history, id, state]);

  // download pdf action
  const downloadPdfAction = useCallback(
    (closeMenu: () => void, itemsOnly = false) => {
      const label = isDraft
        ? 'Lataa luonnos'
        : `Lataa PDF ${itemsOnly ? '(tuotteet)' : ''}`;
      const downloadFn = itemsOnly ? downloadItemsPdf : downloadPdf;
      return (
        <ActionMenuItem
          key={itemsOnly ? 'downloadItemsPdf' : 'downloadPdf'}
          disabled={isFetchingItemsPdf || isFetchingPdf || loading}
          icon={PictureAsPdf}
          label={label}
          loading={itemsOnly ? isFetchingItemsPdf : isFetchingPdf}
          onClick={() => downloadFn().then(() => closeMenu())}
        />
      );
    },
    [
      isDraft,
      downloadItemsPdf,
      downloadPdf,
      isFetchingPdf,
      isFetchingItemsPdf,
      loading,
    ],
  );

  const offerActions = useCallback(() => {
    return (
      <>
        {/* SET RESPONSIBLE PERSON */}
        {state === OfferState.Open && (
          <DefaultTooltip
            title={'Aseta tarjoukselle vastuuhenkilö'}
            placement={'top'}
          >
            <IconButton
              size={'small'}
              color={'primary'}
              onClick={() => setResponsiblePersonDialogOpen(true)}
              sx={{ fontSize: '1.25rem', p: '7px' }}
            >
              <PersonAddAlt1 fontSize={'inherit'} />
            </IconButton>
          </DefaultTooltip>
        )}
        {/* EDIT */}
        <DefaultTooltip title={'Muokkaa'} placement={'top'}>
          <IconButton
            size={'small'}
            color={'primary'}
            disabled={!!editDisabled || loading}
            onClick={handleEdit}
            sx={{ fontSize: '1.25rem', p: '7px' }}
          >
            <Edit fontSize={'inherit'} />
          </IconButton>
        </DefaultTooltip>

        <DefaultTooltip title={'Tarkastele'} placement={'top'}>
          <IconButton
            size={'small'}
            color={'primary'}
            sx={{ fontSize: '1.25rem', p: '7px' }}
            onClick={() => {
              window.open(`/tarjous/${id}/tarkastele`, '_blank');
            }}
          >
            <RemoveRedEye fontSize={'inherit'} />
          </IconButton>
        </DefaultTooltip>
        <ActionMenu
          toggleIcon={<MoreVert />}
          renderItems={(closeMenu) => {
            return [
              // DOWNLOAD PDF
              downloadPdfAction(closeMenu),
              // DOWNLOAD PDF (items only)
              ...(!isDraft
                ? [downloadPdfAction(closeMenu, true)]
                : []),
            ];
          }}
        />
      </>
    );
  }, [
    isDraft,
    downloadPdfAction,
    editDisabled,
    loading,
    handleEdit,
    id,
    state,
  ]);

  return (
    <DataTableRowActionsContainer
      sx={{
        justifyContent: 'end',
        px: 1,
      }}
    >
      <ResponsiblePersonDialog
        refetchOffers={refetch}
        offerSheet={item}
        open={responsiblePersonDialogOpen}
        title={'Tarjouspyynnön käsittely'}
        onClose={handleResponsiblePersonDialogClose}
      />
      {!loading && offerActions()}
      {infoToggle}
    </DataTableRowActionsContainer>
  );
};
